import { useQuery } from '@apollo/client'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, Paper, Popper, Typography } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { AllProjectResponse, Assignment } from 'Models/project'
import { GetProjectTeamQuery } from 'api/graphQL/queries/project.queries'
import React from 'react'
import { LODS, STATUS } from 'shared/utils/dropdownUtils'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import AutoCompleteElementProperties from '../AutoCompleteElementProperties'

const useStyles = makeStyles((theme: Theme) => ({
    actionButton: {
        color: '#A78966',
        fontSize: pxToRem(18),
        width: 108,
        marginBottom: 16,
    },
    advancedHeader: {
        color: '#5E5F61',
        fontSize: pxToRem(16),
    },
    actionButtonContainer: {
        position: 'absolute',
        right: pxToRem(1),
    },
    clearButton: {
        justifyContent: 'start',
    },
    searchButton: {
        justifyContent: 'flex-end',
        paddingRight: theme.spacing(0.9),
    },
    popperPaper: {
        border: '1px solid #DBDFE0',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        borderRadius: pxToRem(4),
        width: pxToRem(1000),
    },
}))

interface AdvancedSearchProps {
    anchorEl: Element | null
    projectId: string | undefined
}
interface AssignedUserList {
    userName: string
    userId?: string | null
}
const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
    anchorEl,
    projectId,
}) => {
    const classes = useStyles()
    const popperRef = React.createRef<any>()

    const { data: projectData } = useQuery<AllProjectResponse>(
        GetProjectTeamQuery,
        {
            variables: {
                projectId: projectId,
            },
        }
    )

    const usersList: AssignedUserList[] = React.useMemo(() => {
        if (projectData) {
            let usersList: any = []
            projectData.getProject.projectTeam?.assignments?.forEach(
                (assignedUser: Partial<Assignment>) => {
                    const obj = {
                        userName:
                            assignedUser.user?.profileInfo?.firstName +
                            ' ' +
                            assignedUser.user?.profileInfo?.lastName,
                        userId: assignedUser.user?.id,
                    }
                    usersList.push(obj)
                }
            )
            usersList.push({
                userName: 'Unassigned',
                userId: null,
            })
            usersList = usersList.sort(
                (a: AssignedUserList, b: AssignedUserList) =>
                    -b.userName.localeCompare(a.userName)
            )
            return usersList
        }

        return [{ userName: 'Unassigned', userId: null }]
    }, [projectData])

    return (
        <Popper
            placement="bottom-start"
            popperRef={popperRef}
            data-testid="advancedSearchPopper"
            anchorEl={anchorEl}
            open={!!anchorEl}
            disablePortal
            modifiers={{
                offset: {
                    enabled: true,
                    offsetRight: 800,
                },
            }}
        >
            <Paper className={classes.popperPaper}>
                <GridContainer direction="column" spacing={3}>
                    <GridItem>
                        <Typography
                            component="span"
                            className={classes.advancedHeader}
                            data-testid="advancedFiltersTitle"
                        >
                            Advanced Filters:
                        </Typography>
                    </GridItem>
                    <GridItem>
                        <GridContainer spacing={4}>
                            <GridItem>
                                <GridContainer direction="column">
                                    <GridItem>
                                        <AutoCompleteElementProperties
                                            isDisabled={false}
                                            placeholderText={'Assignment'}
                                            dropdownList={usersList?.map(
                                                (users) => users.userName
                                            )}
                                            parameterName={'assignedUserId'}
                                            name="assignedToTestId"
                                            selectedValue={null}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <AutoCompleteElementProperties
                                            isDisabled={false}
                                            placeholderText={'LOD'}
                                            dropdownList={LODS}
                                            parameterName={'currentLOD'}
                                            name="currentLODTestId"
                                            selectedValue={null}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem>
                                <GridContainer direction="column">
                                    <GridItem>
                                        <AutoCompleteElementProperties
                                            isDisabled={false}
                                            placeholderText={'Status'}
                                            dropdownList={STATUS.map(
                                                (item) => item.name
                                            )}
                                            parameterName={'state'}
                                            name="stateTestId"
                                            selectedValue={null}
                                            handleChange={() => {}}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <AutoCompleteElementProperties
                                            isDisabled={false}
                                            placeholderText="Target LOD"
                                            dropdownList={LODS}
                                            parameterName={'targetLOD'}
                                            name="targetLODTestId"
                                            selectedValue={null}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem>
                        <GridContainer>
                            <GridItem>
                                <Button
                                    className={classes.actionButton}
                                    classes={{ label: classes.clearButton }}
                                    data-testid="clearButton"
                                >
                                    Clear
                                </Button>
                            </GridItem>
                            <GridItem className={classes.actionButtonContainer}>
                                <GridContainer>
                                    <GridItem>
                                        <Button
                                            className={classes.actionButton}
                                            data-testid="cancelButton"
                                        >
                                            Cancel
                                        </Button>
                                    </GridItem>
                                    <GridItem>
                                        <Button
                                            disableRipple
                                            className={classes.actionButton}
                                            classes={{
                                                label: classes.searchButton,
                                            }}
                                            data-testid="searchButton"
                                        >
                                            Search
                                        </Button>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </Paper>
        </Popper>
    )
}

export default AdvancedSearch
